import { useState, useEffect } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { htmlToMarkdown } from '@/utils/htmlUtils';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { sendEmail } from '@/services/emailSendingService';
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { Loader2, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useDefaultSender } from '@/hooks/useDefaultSender';
import { Link } from 'react-router-dom';
import { useOperation } from '@/hooks/useOperation';

interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
  leadEmail: string;
  defaultSubject: string;
  defaultBody: string;
  fromName?: string;
  fromEmail?: string;
  onEmailSent?: () => void;
}

export const SendEmailDialog = ({
  open,
  onOpenChange,
  leadId,
  leadName,
  leadEmail,
  defaultSubject,
  defaultBody,
  fromName,
  fromEmail,
  onEmailSent,
}: SendEmailDialogProps) => {
  const { toast } = useToast();
  const { defaultSender, isLoading: loadingDefaultSender } = useDefaultSender();
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [senderName, setSenderName] = useState(fromName || '');
  const [senderEmail, setSenderEmail] = useState(fromEmail || '');
  const [recipientEmail, setRecipientEmail] = useState(leadEmail);
  const [showPreview, setShowPreview] = useState(false);
  const [needsDomainVerification, setNeedsDomainVerification] = useState(false);

  const { execute: executeSendEmail, isLoading: sending } = useOperation({
    operation: async () => {
      const markdownBody = htmlToMarkdown(body);
      
      const result = await sendEmail({
        lead_id: leadId,
        subject: subject.trim(),
        body: markdownBody.trim(),
        from_name: senderName.trim() || undefined,
        from_email: senderEmail.trim() || undefined,
        reply_to: senderEmail.trim() || undefined,
        to_email: recipientEmail.trim(),
      });

      if (result && 'error' in result) {
        throw new Error(result.error);
      }

      return result;
    },
    loadingMessage: `Sending email to ${leadName}...`,
    successMessage: `Email sent successfully to ${leadName}`,
    onSuccess: () => {
      onOpenChange(false);
      onEmailSent?.();
    },
    onError: (error: Error) => {
      let errorMessage = error.message || 'An error occurred while sending the email';
      
      if (errorMessage.toLowerCase().includes('domain not verified') ||
          errorMessage.toLowerCase().includes('email settings')) {
        setNeedsDomainVerification(true);
      }
    },
  });

  // Sync state with props when dialog opens or defaults change
  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setBody(defaultBody);
      setRecipientEmail(leadEmail);
      setShowPreview(false);
      setNeedsDomainVerification(false);
      
      // Auto-populate with default sender if available
      if (defaultSender) {
        setSenderName(defaultSender.from_name);
        setSenderEmail(defaultSender.from_email);
      } else if (fromName || fromEmail) {
        // Fallback to provided values
        setSenderName(fromName || '');
        setSenderEmail(fromEmail || '');
      }
    }
  }, [open, defaultSubject, defaultBody, fromName, fromEmail, leadEmail, defaultSender]);

  const handleSend = async () => {
    if (!recipientEmail.trim() || !recipientEmail.includes('@')) {
      toast({
        title: 'Invalid Email',
        description: 'Please provide a valid recipient email address',
        variant: 'destructive',
      });
      return;
    }

    if (!subject.trim() || !body.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please provide both subject and body',
        variant: 'destructive',
      });
      return;
    }

    setNeedsDomainVerification(false);
    await executeSendEmail();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Email to {leadName}
          </DialogTitle>
          <DialogDescription>
            Review and send your email to {leadEmail}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {defaultSender && (
            <Alert className="bg-muted/50 border-primary/20">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription className="flex items-center gap-2">
                <span className="text-sm">Using default sender:</span>
                <Badge variant="secondary" className="font-mono text-xs">
                  {defaultSender.from_email}
                </Badge>
              </AlertDescription>
            </Alert>
          )}

          {!defaultSender && !loadingDefaultSender && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                No default sender configured.{" "}
                <Link to="/profile" className="underline text-primary hover:text-primary/80">
                  Set up sender email
                </Link>
              </AlertDescription>
            </Alert>
          )}

          {needsDomainVerification && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Email Domain Not Verified</AlertTitle>
              <AlertDescription>
                Your company's email domain needs to be verified before you can send emails. 
                Please contact your company admin to verify the domain in <strong>Admin Settings → Email Settings</strong>.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-5">
...
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
